import { useEffect, useRef } from "react";

/**
 * This effect will watch for a "start condition" to be true, before creating
 * an interval timeout to call the provided callback method. The interval is
 * cancelled when the "finish condition" is true.
 *
 * The initial usecase for such an effect was: after the move to asynchronous
 * 'BidProcessingComplete' messages, it was possible for some of those messages
 * to be dropped - causing in the "submitting bid" dialog to stay open forever
 * and not realize that the bid was complete.  We needed a way to fallback and
 * ping or query the bid at some interval until we've confirmed it was finished
 * processing.
 */
function useTimeoutFallbackEffect(
    startCondition: boolean,
    finishCondition: boolean,
    timeout: number,
    callback: () => void
): void {
    const savedCallback = useRef(callback);
    const timeoutId = useRef<NodeJS.Timeout>();

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        if (startCondition && !timeoutId.current) {
            timeoutId.current = setInterval(
                () => savedCallback.current(),
                timeout
            );
        } else if (finishCondition && !!timeoutId.current) {
            clearInterval(timeoutId.current);
            timeoutId.current = undefined;
        }

        return () => clearInterval(timeoutId.current);
    }, [startCondition, finishCondition, timeout]);
}

export default useTimeoutFallbackEffect;
